import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getIsVehicleDetailsOpen,
    getSelectedVehicle,
    getVehicleDetailsPageReference,
    setVehicleDetailsPageReference,
} from 'store/slices/appSlice';

import { closeVehicleDetailsWindow, navigateToVehicleDetails } from './utils';

// When the main window closes it also closes the detail window (see the unload effect). Closing the
// detail window makes it dispatch setIsVehicleDetailsOpen(false) + clearSelectedVehicle on its own
// unload, and redux-state-sync echoes those back to this still-closing main window. For a brief
// moment after we start closing we therefore ignore reopen requests, so that echo cannot re-open a
// blank detail window as the main window goes away. It is timestamp-based (not a latching flag), so
// it self-clears: a normal open on start, after login or when the user re-opens is never affected,
// because the timestamp is only ever set on the main window's own unload.
const REOPEN_SUPPRESSION_MS = 2000;

const useMultimonitorSetup = (missionId?: string) => {
    const selectedVehicle = useSelector(getSelectedVehicle);
    const vin = selectedVehicle?.selectedVIN;
    const dispatch = useDispatch();
    const isVehicleDetailPageOpen = useSelector(getIsVehicleDetailsOpen);
    const vehicleDetailsReference = useSelector(getVehicleDetailsPageReference);
    const mainWindowClosingAt = useRef(0);

    useEffect(() => {
        // Open the detail window whenever it is not open (start, after login, or after the user
        // closed it), except during the brief suppression window right after the main window began
        // closing the detail window itself.
        if (Date.now() - mainWindowClosingAt.current < REOPEN_SUPPRESSION_MS) return;
        const tabId = navigateToVehicleDetails(isVehicleDetailPageOpen, vin);
        if (tabId !== undefined) {
            dispatch(setVehicleDetailsPageReference(tabId));
        }
    }, [isVehicleDetailPageOpen, vin, missionId, dispatch]);

    useEffect(() => {
        const handleUnload = () => {
            mainWindowClosingAt.current = Date.now();
            closeVehicleDetailsWindow(vehicleDetailsReference);
        };
        window.addEventListener('beforeunload', handleUnload);
        return () => window.removeEventListener('beforeunload', handleUnload);
    }, [vehicleDetailsReference]);
};

export default useMultimonitorSetup;
